<template>
    <v-dialog v-model="dialog" max-width="600px" scrollable content-class="z-full-width-dialog">
        <v-card>
            <v-card-title class="headline flex-column pa-2">
                <div class="d-flex fill-width">
                    <div class="flex-grow-1">{{$translate('Manage team settings')}}</div>
                    <v-btn icon @click="dialog = false" class="float-right"><v-icon>mdi-close</v-icon></v-btn>
                </div>
            </v-card-title>

            <v-card-text>
                <v-row no-gutters>
                    <v-col md="3" cols=12>
                        <div class="d-flex flex-column align-center">
                            <img :src="$api.getImageUrl(group.imagekey, 400)" style="background: white; width: 100px; height: 100px" />
                            <a href="#" @click.prevent="showChangeLogoDialog = true">Ändra bild</a>
                        </div>
                    </v-col>
                    <v-col md="9" cols=12>
                        <div class="font-weight-bold" style="padding-left: 24px;">{{$translate('Leaders')}}</div>
                        <multi-column-list :items="changeTeamLeaders ? sortedTeamMembersNotRemoved : sortedLeadersNotRemoved">
                            <template #icon="{item: user}">
                                <div class="d-flex align-items-center">
                                    <div style="width: 24px; height: 24px;">
                                        <v-icon
                                            v-if="values.responsible[user.id] && changeTeamLeaders"
                                            :color="values.user_types[user.id] === 'mainLeader' ? 'primary' : 'grey'"
                                            @click.stop="assignLeaderType(user.id)">
                                            {{ values.user_types[user.id] === 'mainLeader' ? 'mdi-star' : 'mdi-star-outline' }}
                                        </v-icon>
                                    </div>
                                    <v-simple-checkbox v-model="values.responsible[user.id]" color="primary" v-if="changeTeamLeaders" />
                                    <div style="position: relative; display: inline-flex;">
                                        <v-avatar :size="20">
                                            <img :src="'/api/memberapi/image/member/get?size=50x50&id=' + user.id" class="user-image" />
                                        </v-avatar>
                                        <v-icon
                                            small
                                            style="position: absolute; top: 0; left: 0; transform: translate(-50%, -50%);"
                                            color="primary"
                                            v-if="values.user_types[user.id] === 'mainLeader'">
                                            mdi-star
                                        </v-icon>
                                    </div>
                                </div>
                            </template>
                            <template #append v-if="!changeTeamLeaders">
                                <div class="d-flex z-clickable" key="add" @click="changeTeamLeaders = true" v-ripple="{center: true}" style="margin-left: 24px;">
                                    <div class="mr-2">
                                        <v-icon color="primary">mdi-pencil</v-icon>
                                    </div>
                                    <div>
                                        <div>{{$translate('Change team leaders')}}</div>
                                    </div>
                                </div>
                            </template>
                        </multi-column-list>
                        <v-alert class="ma-2" dense outlined type="info" v-if="changeTeamLeaders">
                            {{ $translate('Assigning a star to a team leader makes them the primary contact person') }}
                        </v-alert>
                    </v-col>
                    <v-col md="9" cols=12 class="mt-4">
                        <div class="font-weight-bold">{{$translate('Team members')}}</div>
                        <multi-column-list :items="sortedTeamMembersNotRemoved">
                            <template #icon="{item: user}">
                                <div class="d-flex">
                                    <v-icon small @click="addUserToRemoveList(user)" color="primary" v-if="removeTeamMembers" class="mr-1">
                                        mdi-trash-can-outline
                                    </v-icon>
                                    <v-avatar :size="20">
                                        <img :src="'/api/memberapi/image/member/get?size=50x50&id=' + user.id" class="user-image" />
                                    </v-avatar>
                                </div>
                            </template>
                            <template #append v-if="!removeTeamMembers">
                                <div class="d-flex z-clickable" key="add" @click="removeTeamMembers = true" v-ripple="{center: true}">
                                    <div class="mr-2">
                                        <v-icon color="primary">mdi-trash-can</v-icon>
                                    </div>
                                    <div>
                                        <div>{{$translate('Remove team members')}}</div>
                                    </div>
                                </div>
                            </template>
                        </multi-column-list>
                        <div v-if="sortedRemovedTeamMembers.length > 0" class="mt-4">
                            <div class="font-weight-bold">{{$translate('Team members who will be removed')}}</div>
                            <multi-column-list :items="sortedRemovedTeamMembers">
                                <template #content="{item: user}">
                                    {{user.name}}
                                    <span v-if="sortedLeaders.find(u => u.id === user.id)" class="ml-2">({{$translate('will also be removed as leader')}})</span>
                                </template>
                                <template #icon="{item: user}">
                                    <div class="d-flex">
                                        <v-icon small @click="removeFromRemoveList(user)" color="primary" class="mr-1">
                                            mdi-undo
                                        </v-icon>
                                        <v-avatar :size="20">
                                            <img :src="'/api/memberapi/image/member/get?size=50x50&id=' + user.id" class="user-image" />
                                        </v-avatar>
                                    </div>
                                </template>
                            </multi-column-list>
                        </div>
                        <v-form @submit.prevent="save" v-model="settingsModel">
                            <form-field v-for="field in fields" :field="field" :key="field.name" v-model="values[field.name]" star />
                        </v-form>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="save" :loading="saving" :disabled="!changed">{{$translate('Save')}}</v-btn>
            </v-card-actions>
        </v-card>
        <teamsport-team-logo-form v-if="showChangeLogoDialog" v-model="showChangeLogoDialog" :group="group" />
    </v-dialog>
</template>

<script>
import FormField from '../components/FormField.vue'
import TeamsportTeamLogoForm from './TeamsportTeamLogoForm.vue';
import MultiColumnList from '../components/Teamsport/MultiColumnList.vue';
import {isEqual} from 'lodash';
export default {
    name: 'zoezi-teamsport-team-settings-dialog',

    props: {
        value: {
            type: Boolean,
            default: false
        },
        group: {
            type: Object
        }
    },

    components: {
        FormField,
        TeamsportTeamLogoForm,
        MultiColumnList
    },

    watch: {
        value: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.values = Object.assign({}, this.originalValues);
                    this.values.responsible = Object.assign({}, this.originalValues.responsible)
                    this.values.user_types = Object.assign({}, this.originalValues.user_types)
                    this.values.users = Object.assign({}, this.originalValues.users)
                }
            }
        },
        'values.responsible': {
            deep: true,
            handler(newVal) {
                for (const userId in newVal) {
                    if (newVal[userId] === false) {
                        this.$delete(this.values.user_types, userId);
                    } else if (!this.values.user_types[userId]) {
                        this.$set(this.values.user_types, userId, "viceLeader");
                    }
                }
            }
        }
    },

    data: () => ({
        settingsModel: false,
        values: {
            name: '',
            memberCanInvite: false,
            bookingOption: 'standard',
            responsible: {},
            user_types: {},
            users: {},
        },
        showChangeLogoDialog: false,
        saving: false,
        changeTeamLeaders: false,
        removeTeamMembers: false
    }),

    computed: {
        dialog: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        changed() {
            return Object.keys(this.values).some(key => !isEqual(this.values[key], this.originalValues[key]));
        },
        originalValues() {
            let res = {};
            this.fields.map(field => field.name).forEach(name => res[name] = this.group[name]);
            res.responsible = {};
            this.group.users.forEach(user => {
                res.responsible[user.id] = this.isTeamLeader(user);
            })
            res.users = {};
            this.group.users.forEach(user => {
                res.users[user.id] = true;
            })
            res.user_types = this.group.user_types ? {...this.group.user_types} : {};
            return res;
        },
        fields() {
            return [
                {name: 'memberCanInvite', title: 'Members can invite to group', type: 'checkbox', required: false },
                {name: 'bookingOption', title: 'Invitation options', type: 'select', data: [
                     {value: 'standard', text: this.$translate('Manual invitation from leader')},
                     {value: 'autoInvite', text: this.$translate('Automatic invitation') }],
                 required: true
                }
            ];
        },
        sortedLeaders() {
            return this.group.responsible.concat().sort((a, b) => a.name.localeCompare(b.name));
        },
        sortedLeadersNotRemoved() {
            return this.sortedLeaders.filter(user => this.values.users[user.id]);
        },
        sortedTeamMembers() {
            return this.group.users.concat().sort((a, b) => a.name.localeCompare(b.name));
        },
        sortedTeamMembersNotRemoved() {
            return this.sortedTeamMembers.filter(user => this.values.users[user.id]);
        },
        sortedRemovedTeamMembers() {
            return this.sortedTeamMembers.filter(user => !this.values.users[user.id]);
        }
    },
    methods: {
        assignLeaderType(userId) {
            let currentType = this.values.user_types[userId];
            let newType = currentType === "mainLeader" ? "viceLeader" : "mainLeader";

            this.$set(this.values.user_types, userId, newType);
        },
        save() {
            let data = Object.assign({id: this.group.id}, this.values);
            data.responsible = Object.keys(this.values.responsible).filter(key => this.values.responsible[key] && this.values.users[key]).map(key => parseInt(key));
            data.users = Object.keys(this.values.users).filter(key => this.values.users[key]).map(key => parseInt(key));
            this.saving = true;
            this.$api.post('/api/memberapi/usergroup/change', data).then(() => {
                this.dialog = false;
                this.saving = false;
            });
        },
        isTeamLeader(user) {
            return this.group.responsible.some(u => u.id == user.id);
        },
        addUserToRemoveList(user) {
            this.values.users[user.id] = false;
        },
        removeFromRemoveList(user) {
            this.values.users[user.id] = true;
        }
    }
};
</script>
